import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, ResponsiveContainer, Legend } from 'recharts';
import { Users, UserCheck, Clock, TrendingUp, Target, Briefcase, Filter, Heart, MessageSquare, Battery } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FatigueGauge } from '@/components/common/FatigueGauge';
import { SatisfactionHeart } from '@/components/common/SatisfactionHeart';
import { ComplaintsDialog } from './ComplaintsDialog';
import { useDemographicData } from './hooks/useDemographicData';
import { useTeams } from './hooks/useTeams';

type FilterType = 'all' | 'guards' | 'admins';

const getChartConfig = (t: (key: string) => string) => ({
  guards: {
    label: t("guards"),
    color: "hsl(var(--primary))",
  },
  admins: {
    label: t("admins"), 
    color: "hsl(var(--secondary))",
  },
  male: {
    label: t("male"),
    color: "hsl(220, 70%, 50%)",
  },
  female: {
    label: t("female"), 
    color: "hsl(300, 65%, 60%)",
  },
});

interface DemographicsProps {
  t: (key: string) => string;
}

export function Demographics({ t }: DemographicsProps) {
  console.log('Demographics component rendering...');
  const [selectedFilter, setSelectedFilter] = useState<FilterType>('all');
  const [selectedTeam, setSelectedTeam] = useState<string>('all');
  const [complaintsDialogOpen, setComplaintsDialogOpen] = useState(false);
  
  const { teams } = useTeams();
  const { data, loading, profileIds } = useDemographicData(selectedFilter, selectedTeam, teams);
  const chartConfig = getChartConfig(t);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-16 bg-muted rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-64 bg-muted rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            {t("dataFilters")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium">{t("showDataFor")}</label>
              <Select value={selectedFilter} onValueChange={(value: FilterType) => setSelectedFilter(value)}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("all")}</SelectItem>
                  <SelectItem value="guards">{t("guards")}</SelectItem>
                  <SelectItem value="admins">{t("admins")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium">{t("team")}</label>
              <Select value={selectedTeam} onValueChange={setSelectedTeam}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allTeams")}</SelectItem>
                  {teams.map(team => (
                    <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("activeEmployees")}</p>
                <p className="text-2xl font-bold">{data.totalActive}</p>
              </div>
              <Users className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("avgTenure")}</p>
                <p className="text-2xl font-bold">{data.avgTenure} {t("years")}</p>
              </div>
              <Clock className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("turnoverRate")}</p>
                <p className="text-2xl font-bold">{data.turnoverRate}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("avgBusinessExperience")}</p>
                <p className="text-2xl font-bold">{data.avgBusinessExperience} {t("years")}</p>
              </div>
              <Briefcase className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("avgSatisfaction")}</p>
                <p className="text-2xl font-bold">{data.avgSatisfactionRating}/7</p>
                <p className="text-sm text-muted-foreground">{t("last30Days")}</p>
              </div>
              <div className="flex-shrink-0">
                <SatisfactionHeart score={data.avgSatisfactionRating} maxScore={7} size={48} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => setComplaintsDialogOpen(true)}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("complaints90d")}</p>
                <p className="text-2xl font-bold">{data.totalComplaints}</p>
                <p className="text-sm text-muted-foreground">{data.pendingComplaints} {t("pending")}</p>
              </div>
              <MessageSquare className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("avgFatigueLevel")}</p>
              </div>
              <div className="flex-shrink-0">
                <FatigueGauge value={data.avgFatigueLevel} size={150} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("complaintResolutionRate")}</p>
                <p className="text-2xl font-bold">{data.complaintResolutionData.resolutionRate}%</p>
                <p className="text-sm text-muted-foreground">{t("target")}: {data.complaintResolutionData.target}%</p>
                <div className="mt-2 text-xs text-muted-foreground">
                  <div>{t("withSatisfaction")}: {data.complaintResolutionData.resolvedWithSatisfaction}</div>
                  <div>{t("withoutSatisfaction")}: {data.complaintResolutionData.resolvedWithoutSatisfaction}</div>
                </div>
              </div>
              <div className="flex flex-col items-center">
                <Target className="h-8 w-8 text-primary" />
                <div className={cn(
                  "text-xs mt-1 px-2 py-1 rounded",
                  data.complaintResolutionData.resolutionRate >= data.complaintResolutionData.target 
                    ? "bg-green-100 text-green-700" 
                    : "bg-yellow-100 text-yellow-700"
                )}>
                  {data.complaintResolutionData.resolutionRate >= data.complaintResolutionData.target ? t("onTarget") : t("belowTarget")}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gender Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>{t("genderDistribution")}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.genderData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percentage }) => `${name}: ${percentage}%`}
                  >
                    {data.genderData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <ChartTooltip content={<ChartTooltipContent />} />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Age Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>{t("ageDistribution")}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.ageRangeData}>
                  <XAxis dataKey="range" />
                  <YAxis />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="count">
                    {data.ageRangeData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={[
                          "hsl(220, 70%, 50%)",  // Blue
                          "hsl(260, 65%, 55%)",  // Purple
                          "hsl(300, 60%, 60%)",  // Pink
                          "hsl(340, 70%, 55%)",  // Red-pink
                          "hsl(20, 75%, 60%)"    // Orange
                        ][index % 5]} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Tenure Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>{t("tenureDistribution")}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.tenureData}>
                  <XAxis dataKey="range" />
                  <YAxis />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="count">
                    {data.tenureData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={[
                          "hsl(180, 65%, 50%)",  // Teal
                          "hsl(160, 70%, 45%)",  // Green
                          "hsl(80, 75%, 55%)",   // Lime
                          "hsl(45, 80%, 60%)",   // Yellow
                          "hsl(25, 75%, 55%)"    // Orange
                        ][index % 5]} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Turnover Trends */}
        <Card>
          <CardHeader>
            <CardTitle>{t("turnoverTrends")}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.turnoverData}>
                  <XAxis dataKey="month" />
                  <YAxis />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Legend />
                  <Line type="monotone" dataKey="hires" stroke="hsl(var(--primary))" name={t("hires")} />
                  <Line type="monotone" dataKey="departures" stroke="hsl(var(--destructive))" name={t("departures")} />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Complaints Dialog */}
      <ComplaintsDialog
        open={complaintsDialogOpen}
        onOpenChange={setComplaintsDialogOpen}
        profileIds={profileIds || []}
      />
    </div>
  );
}